// mow-frontend/src/components/analytics/SupplierPerformanceWidget.js
import React, { useState, useEffect } from 'react';
import {
  Box, Heading, HStack, Icon, Spinner, Alert, AlertIcon, Text, Table, Thead, Tbody,
  Tr, Th, Td, Tag, useColorModeValue, TableContainer, Tooltip
} from '@chakra-ui/react';
import { FaArrowUp, FaArrowDown, FaEquals } from 'react-icons/fa';
import analyticsService from '../../services/analyticsService';
import Card from '../card/Cards';

const SupplierPerformanceWidget = () => {
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const textColor = useColorModeValue('gray.700', 'white');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await analyticsService.getSupplierPerformance();
        if (response.success) {
          // Sort suppliers by on-time delivery rate, descending
          response.data.sort((a, b) => (b.performance?.onTimeDeliveryRate || 0) - (a.performance?.onTimeDeliveryRate || 0));
          setPerformanceData(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch supplier performance.');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const VarianceIndicator = ({ value }) => {
    const numValue = parseFloat(value);
    if (numValue > 2) return <Icon as={FaArrowUp} color="red.500" />; // Late or over budget
    if (numValue < -2) return <Icon as={FaArrowDown} color="green.500" />; // Early or under budget
    return <Icon as={FaEquals} color="gray.500" />;
  };

  const renderContent = () => {
    if (loading) return <Spinner />;
    if (error) return <Alert status="error"><AlertIcon />{error}</Alert>;
    if (performanceData.length === 0) {
      return <Text color="gray.500" p={4}>No supplier performance data available.</Text>;
    }
    return (
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Supplier</Th>
              <Th isNumeric>On-Time Rate</Th>
              <Th isNumeric>Lead Time Variance (Days)</Th>
              <Th isNumeric>Cost Variance (%)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {performanceData.map((supplier) => (
              <Tr key={supplier.id}>
                <Td fontWeight="bold">{supplier.name}</Td>
                {supplier.performance ? (
                  <>
                    <Td isNumeric>
                        <Tag colorScheme={supplier.performance.onTimeDeliveryRate >= 95 ? 'green' : 'yellow'}>
                            {supplier.performance.onTimeDeliveryRate}%
                        </Tag>
                    </Td>
                    <Td isNumeric>
                        <HStack justify="flex-end">
                            <Text>{supplier.performance.avgLeadTimeVariance}</Text>
                            <VarianceIndicator value={supplier.performance.avgLeadTimeVariance} />
                        </HStack>
                    </Td>
                    <Td isNumeric>
                        <HStack justify="flex-end">
                            <Text>{supplier.performance.avgCostVariance}</Text>
                            <VarianceIndicator value={supplier.performance.avgCostVariance} />
                        </HStack>
                    </Td>
                  </>
                ) : (
                  <Td colSpan={3} textAlign="center" fontStyle="italic" color="gray.500">No completed orders to analyze</Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Card p={5} h="100%">
      <Heading size="md" mb={4} color={textColor}>Supplier Performance</Heading>
      {renderContent()}
    </Card>
  );
};

export default SupplierPerformanceWidget;